// Stage 2 "Dashboard" · 2.3 (e) — Risk heat map: FS head × engagement; legend significant / high / medium / low; each cell opens the
// risk register for that head. An engagement with "no_risks_linked" shows the sentence, never an empty grid.
import React from "react";
import { fmtNumber } from "@/lib/format";
import { FD, HEAT_SHADE, heatLevel, TARGET, fill } from "./fdTheme";
import { FdLink, Panel, Td, Empty } from "./FdPrimitives";
import { FdResizableTable, FdRTh } from "./FdResizable";   // Step 11d · add-on: adjustable columns
import { useResizableColumns } from "@/components/ui/resizable-table";

export default function FdHeatMap({ rows, t, lang }) {
  const list = Array.isArray(rows) ? rows : [];
  // the union of FS heads across the engagements, in first-seen order
  const heads = [];
  const seen = new Set();
  for (const r of list) {
    if (!Array.isArray(r.heads)) continue;
    for (const h of r.heads) if (h && h.fs_code && !seen.has(h.fs_code)) { seen.add(h.fs_code); heads.push(h); }
  }
  const headName = (h) => (lang === "ar" && h.name_ar) || h.name || h.fs_code;
  // Step 11d · add-on: the client column + one column per FS head, adjustable (one key per head count; was 24 % + an equal share)
  const heatCols = useResizableColumns({ storageKey: `dashboard-heatmap-${heads.length}`, defaults: [260, ...heads.map(() => 120)],
                                         mins: [140, ...heads.map(() => 80)] });
  const n = (v) => fmtNumber(v || 0, lang);
  const legend = (
    <div data-testid="fd-heat-legend" className="flex flex-wrap items-center gap-2 text-xs" style={{ color: FD.muted }}>
      <span>{t("fd.heat.legend")}:</span>
      {["significant", "high", "medium", "low"].map((k) => (
        <span key={k} className="inline-flex items-center gap-1"><i className="inline-block h-3 w-3 rounded-sm" style={{ background: HEAT_SHADE[k].bg }} />{t(`fd.heat.${k}`)}</span>
      ))}
    </div>
  );
  return (
    <Panel title={t("fd.heat.title")} testid="fd-heatmap" right={legend}>
      {list.length === 0 ? <Empty>{t("fd.heat.empty")}</Empty> : (
        <FdResizableTable cols={heatCols} testid="fd-heat-table">
          <thead>
            <tr>
              <FdRTh index={0}>{t("fd.portfolio.client")}</FdRTh>
              {heads.map((h, i) => <FdRTh key={h.fs_code} index={i + 1} align="center" className="normal-case tracking-normal"><span className="block text-[10px]" style={{ color: FD.muted }}>{h.fs_code}</span>{headName(h)}</FdRTh>)}
            </tr>
          </thead>
          <tbody>
            {list.map((r) => {
              const id = r.engagement_id;
              if (!Array.isArray(r.heads)) {
                return (
                  <tr key={id}>
                    <Td className="font-medium"><FdLink to="/risk" engId={id}>{r.client_name}</FdLink></Td>
                    <Td colSpan={Math.max(1, heads.length)} className="italic" style={{ color: FD.muted }}>
                      <FdLink to="/risk" engId={id} testid={`fd-heat-empty-${id}`} style={{ color: FD.muted }}>{t("fd.heat.none")}</FdLink>
                    </Td>
                  </tr>
                );
              }
              const byCode = Object.fromEntries(r.heads.map((h) => [h.fs_code, h]));
              return (
                <tr key={id}>
                  <Td className="font-medium"><FdLink to="/risk" engId={id}>{r.client_name}</FdLink></Td>
                  {heads.map((h) => {
                    const cell = byCode[h.fs_code]; const lvl = heatLevel(cell); const sh = HEAT_SHADE[lvl];
                    return (
                      <Td key={h.fs_code} align="center" className="p-1">
                        <FdLink to={TARGET.riskHead(h.fs_code)} engId={id} className="rounded px-1 py-1.5 text-sm font-semibold tabular-nums"
                                style={{ background: sh.bg, color: sh.fg }} title={cell ? fill(t("fd.heat.risks"), { n: n(cell.count) }) : undefined}>
                          <span data-testid={`fd-heat-${id}-${h.fs_code}`} data-level={lvl}>{cell && cell.count ? n(cell.count) : "·"}</span>
                        </FdLink>
                      </Td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </FdResizableTable>
      )}
    </Panel>
  );
}
